import { ref, type Ref } from 'vue'

/**
 * Shared HTML5 drag-and-drop reordering composable for sortable lists.
 *
 * Usage:
 * ```ts
 * const list = ref<SomeItem[]>([])
 * const { draggingId, onDragStart, onDragOver, onDrop, onDragEnd } =
 *   useDragSort(list, { enabled: canEdit, onReorder: persist })
 * ```
 *
 * Rows opt in with `draggable`, wire the handlers, and bind the row class to
 * `draggingId === item.id` / `overId === item.id` for visual feedback.
 * After a drop the composable mutates `list` to the new order and invokes
 * `onReorder(movedItem, fromIndex, toIndex, reordered)` so the caller can
 * persist `sort_order` (or any other ordering field).
 */
export interface UseDragSortOptions<T> {
  /** Static or reactive flag controlling whether dragging is allowed (e.g. permission gate). */
  enabled?: Ref<boolean> | boolean
  /** Namespace for the HTML5 dataTransfer payload; keep unique per list/page. */
  dataKey?: string
  /**
   * Called after an item lands at a new position, with the already-reordered
   * list. Return a promise to await persistence.
   */
  onReorder?: (
    movedItem: T,
    fromIndex: number,
    toIndex: number,
    reordered: T[],
  ) => void | Promise<void>
}

export interface UseDragSortReturn<T> {
  /** Id of the row currently being dragged. */
  draggingId: Ref<number | string | null>
  /** Id of the row currently hovered as a drop target. */
  overId: Ref<number | string | null>
  /** Call on the source row's `@dragstart`. */
  onDragStart: (event: DragEvent, item: T) => void
  /** Call on each row's `@dragover`. */
  onDragOver: (event: DragEvent, item: T) => void
  /** Call on the target row's `@drop`. */
  onDrop: (event: DragEvent, item: T) => void
  /** Call on `@dragend` (and `@dragleave`) to clear drag state. */
  onDragEnd: () => void
  /** Programmatically move an item from one index to another. */
  moveItem: (fromIndex: number, toIndex: number) => void
}

export function useDragSort<T extends { id: number | string }>(
  list: Ref<T[]>,
  options: UseDragSortOptions<T> = {},
): UseDragSortReturn<T> {
  const draggingId = ref<number | string | null>(null)
  const overId = ref<number | string | null>(null)
  const dataKey = options.dataKey ?? 'application/x-yuksalish-sort'

  function isEnabled(): boolean {
    if (typeof options.enabled === 'boolean') {
      return options.enabled
    }
    return options.enabled?.value ?? true
  }

  function moveItem(fromIndex: number, toIndex: number): void {
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= list.value.length ||
      toIndex >= list.value.length ||
      fromIndex === toIndex
    ) {
      return
    }
    const reordered = [...list.value]
    const [moved] = reordered.splice(fromIndex, 1)
    reordered.splice(toIndex, 0, moved)
    list.value = reordered
    void options.onReorder?.(moved, fromIndex, toIndex, reordered)
  }

  function onDragStart(event: DragEvent, item: T): void {
    if (!isEnabled()) return
    draggingId.value = item.id
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData(dataKey, String(item.id))
    }
  }

  function onDragOver(event: DragEvent, item: T): void {
    if (!isEnabled() || draggingId.value == null) return
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    if (overId.value !== item.id) {
      overId.value = item.id
    }
  }

  function onDrop(event: DragEvent, item: T): void {
    if (!isEnabled() || draggingId.value == null) return
    event.preventDefault()
    const payload = event.dataTransfer?.getData(dataKey)
    const draggedId =
      payload != null && payload !== ''
        ? (Number.isNaN(Number(payload)) ? payload : Number(payload))
        : draggingId.value
    if (draggedId != null) {
      const fromIndex = list.value.findIndex((entry) => entry.id === draggedId)
      const toIndex = list.value.findIndex((entry) => entry.id === item.id)
      moveItem(fromIndex, toIndex)
    }
    draggingId.value = null
    overId.value = null
  }

  function onDragEnd(): void {
    draggingId.value = null
    overId.value = null
  }

  return {
    draggingId,
    overId,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    moveItem,
  }
}
